import React, { FC, useCallback, useEffect, useRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { cn } from '@utils/cn';
import { isProbablyHtml, normalizeRichTextForSubmit, plainTextToHtml } from '@utils/richText';
import { Toolbar } from './Toolbar';

export interface RichTextEditorProps {
  name?: string;
  id?: string;
  /** Sanitized HTML, or a legacy plain-text value — the editor detects and converts. */
  value?: string | null;
  /** Emits HTML, or null once the document renders as nothing. */
  onChange: (html: string | null) => void;
  onBlur?: () => void;
  placeholder?: string;
  /**
   * Injected as a boolean by molecules/form/FormGroup, which does
   * `cloneElement(child, { error: !!error, ...child.props })`.
   */
  error?: boolean;
  disabled?: boolean;
  className?: string;
  editorClassName?: string;
  minHeightClassName?: string;
}

/** Matches the debounce below: long enough to skip per-keystroke work, short enough to feel live. */
const CHANGE_DEBOUNCE_MS = 300;

const toEditorContent = (value?: string | null): string => {
  if (!value) {
    return '';
  }
  return isProbablyHtml(value) ? value : plainTextToHtml(value);
};

/**
 * Rich-text field for task descriptions. Emits the same HTML the mobile editor emits, because
 * both run Tiptap 3 over the same schema.
 *
 * Placed in `atoms/` rather than `molecules/`: it is one form control with one value in and one
 * value out, and it has to sit as a direct child of `molecules/form/FormGroup` exactly where
 * `atoms/TextareaInput` used to. That matches how the codebase already treats composite
 * third-party controls — `atoms/ReactSelect`, `atoms/GoogleAddressInput`, `atoms/InputPhone`.
 */
export const RichTextEditor: FC<RichTextEditorProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  placeholder = 'Enter task description',
  error = false,
  disabled = false,
  className,
  editorClassName,
  minHeightClassName = 'min-h-32',
}) => {
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const emit = useCallback((html: string) => {
    onChangeRef.current(normalizeRichTextForSubmit(html));
  }, []);

  const editor = useEditor({
    editable: !disabled,
    // Tiptap 3 does not re-render on every transaction by default, which would leave the
    // toolbar's active states stale. The editor subtree is small, so re-rendering it is cheap.
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // Deliberately outside the contract: the mobile editor cannot round-trip these, and
        // ProseMirror drops unknown nodes silently, so allowing them here would mean a task
        // formatted on web lost that formatting the first time it was saved from a phone.
        codeBlock: false,
        code: false,
        strike: false,
        horizontalRule: false,
        link: {
          openOnClick: false,
          autolink: true,
          protocols: ['http', 'https', 'mailto', 'tel'],
          HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
        },
      }),
    ],
    content: toEditorContent(value),
    onUpdate: ({ editor: instance }) => {
      clearTimeout(debounceRef.current);
      const html = instance.getHTML();
      debounceRef.current = setTimeout(() => emit(html), CHANGE_DEBOUNCE_MS);
    },
    onBlur: ({ editor: instance }) => {
      // Flush before the debounce fires, so "type fast then hit Save" cannot drop the last
      // keystrokes.
      clearTimeout(debounceRef.current);
      emit(instance.getHTML());
      onBlur?.();
    },
  });

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  /**
   * Re-seed only when the value changed from the outside — switching to another task, or the
   * form resetting. Calling setContent on every render would destroy the cursor on each
   * keystroke, so the guard compares against what the editor already holds and skips while the
   * user is typing.
   */
  useEffect(() => {
    if (!editor || editor.isFocused) {
      return;
    }
    const next = toEditorContent(value);
    if (next !== editor.getHTML()) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  return (
    <div
      className={cn(
        'w-full rounded-md border bg-white dark:bg-gray-800',
        error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600',
        disabled && 'opacity-50 pointer-events-none',
        className,
      )}>
      <Toolbar editor={editor} disabled={disabled} />
      <EditorContent
        editor={editor}
        id={id}
        // `rich-text` is the shared stylesheet in index.css; without it Tailwind's preflight
        // leaves lists unmarked and headings the same size as body text.
        className={cn(
          'rich-text p-2 text-sm overflow-y-auto max-h-80',
          '[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-full',
          minHeightClassName,
          editorClassName,
        )}
        data-placeholder={placeholder}
        data-testid={name ? `rich-text-editor-${name}` : 'rich-text-editor'}
      />
    </div>
  );
};

export default RichTextEditor;
